import { useCallback, useState } from 'react';
import {
  ButtonColor,
  TextButton,
  ButtonGroup,
  TextInput,
  Icon,
  IconButton,
  ButtonVariant,
  InputError,
} from '../../../components';
import { useProcessUrlMutation } from '../../../services';
import { ComponentSize, FilePurpose } from '../../../types';
import { useHandleApiResponse } from '../../../hooks';
import { isValidURL } from '../../../utils';

const SHARE_MEDIA_LINK_ERROR_MSG = 'Failed to share media links';

interface ShareMediaLinksProps {
  onBack: () => void;
  onSuccess: () => void;
}

const ShareMediaLinks = ({ onBack, onSuccess }: ShareMediaLinksProps) => {
  const [processUrl, { isLoading: isSubmittingLink }] = useProcessUrlMutation();
  const handleApiResponse = useHandleApiResponse();

  const [linkInputs, setLinkInputs] = useState<string[]>(['']);
  const [error, setError] = useState<string>('');

  const handleLinkChange = useCallback(
    (value: string, index: number) => {
      const updatedLinks = [...linkInputs];
      updatedLinks[index] = value;
      setLinkInputs(updatedLinks);
      setError('');
    },
    [linkInputs]
  );

  const handleDeleteLink = useCallback(
    (index: number) => {
      const updatedLinks = [...linkInputs];
      updatedLinks.splice(index, 1);
      setLinkInputs(updatedLinks);
      setError('');
    },
    [linkInputs]
  );

  const handleAddLink = useCallback(() => {
    const currentInputIndex = linkInputs.length - 1;
    const currentInput = linkInputs[currentInputIndex];

    if (!currentInput.trim()) {
      setError('Please enter a valid link');
      return;
    }

    // Add a new empty string for the next input
    setLinkInputs([...linkInputs, '']);
    setError('');
  }, [linkInputs]);

  const handleShareMediaLink = useCallback(async () => {
    if (error) return;

    const invalidLinks = linkInputs.filter((link) => !isValidURL(link));
    if (invalidLinks.length > 0) {
      setError('Please enter valid URLs');
      return;
    }

    try {
      const response = await processUrl({
        urls: linkInputs.map((link) => ({
          url: link,
          purpose: FilePurpose.CALL_CREATION,
        })),
      });
      handleApiResponse({
        response,
        errorMsg: SHARE_MEDIA_LINK_ERROR_MSG,
        onSuccess: onSuccess,
      });
    } catch (error) {
      console.error(error, SHARE_MEDIA_LINK_ERROR_MSG);
    }
  }, [linkInputs, error, processUrl, handleApiResponse, onSuccess]);

  // Count only non-empty links for the import button
  const validLinksCount = linkInputs.filter((link) => link.trim()).length;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        {linkInputs.map((link, index) => (
          <TextInput
            key={index}
            placeholder="https://"
            value={link}
            onChange={(e) => handleLinkChange(e.target.value, index)}
            endElement={
              <IconButton
                icon={Icon.TRASH}
                color={ButtonColor.DESTRUCTIVE}
                variant={ButtonVariant.GHOST}
                size={ComponentSize.X_SMALL}
                disabled={linkInputs.length === 1}
                onClick={() => handleDeleteLink(index)}
              />
            }
          />
        ))}
        <InputError message={error} />
        <TextButton
          startIcon={Icon.PLUS}
          text="Add link"
          onClick={handleAddLink}
          color={ButtonColor.NEUTRAL}
          variant={ButtonVariant.SOFT}
        />
      </div>
      <ButtonGroup>
        <TextButton
          text={`Import ${validLinksCount > 0 ? validLinksCount : ''} call${validLinksCount === 1 ? '' : 's'}`}
          disabled={validLinksCount === 0}
          onClick={handleShareMediaLink}
          loading={isSubmittingLink}
        />
        <TextButton text="Back" onClick={onBack} />
      </ButtonGroup>
    </div>
  );
};

export default ShareMediaLinks;
